import { formatDay, formatDayLong, parseDay } from "../../../components/charts/formatters";

/**
 * The date-range presets of the Admin Dashboards filter bar (ASB-4377, design
 * §6): Yesterday, 7, 30 and 90 days, every one ending yesterday UTC, because
 * the DAG computes a day once it is complete. The preset is what the query
 * string carries (`?range=7d`), so a link from the daily email opens the
 * same view a day later with that day's data.
 *
 * A panel's `dataFrom` (A.6) is its first complete day: a preset reaching
 * before it is clamped to start there on screen (`start`, `clamped`), while
 * the request still sends the preset's own `from`, which the API clamps too.
 */
export const RANGE_PRESETS = [
	{ value: "yesterday", label: "Yesterday", days: 1 },
	{ value: "7d", label: "Past 7 days", days: 7 },
	{ value: "30d", label: "Past 30 days", days: 30 },
	{ value: "90d", label: "Past 90 days", days: 90 },
];
export const DEFAULT_RANGE = "7d";

export const presetFor = (value) =>
	RANGE_PRESETS.find((p) => p.value === value) ??
	RANGE_PRESETS.find((p) => p.value === DEFAULT_RANGE);

const DAY_MS = 86400000;
const iso = (d) => d.toISOString().slice(0, 10);

/**
 * `{ preset, from, to, days, start, dataFrom, clamped }` for a preset value,
 * as of `now` (UTC). An unknown value is the default. `start` is the first
 * day shown: `from`, or `dataFrom` when the preset reaches before it (and
 * never after `to`).
 */
export function rangeFor(value, now = new Date(), dataFrom = null) {
	const preset = presetFor(value);
	const yesterday = new Date(
		Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - 1)
	);
	const from = new Date(yesterday.getTime() - (preset.days - 1) * DAY_MS);
	const range = { preset: preset.value, from: iso(from), to: iso(yesterday), days: preset.days };
	const floor = parseDay(dataFrom) ? String(dataFrom).slice(0, 10) : null;
	const clamped = Boolean(floor && floor > range.from);
	range.start = clamped ? (floor > range.to ? range.to : floor) : range.from;
	range.dataFrom = floor;
	range.clamped = clamped;
	return range;
}

/**
 * "Wed, Oct 8, 2026" for one day; "Oct 2 – Oct 8, 2026" for a span (from the
 * clamped start). A span across New Year carries both years, "Dec 26, 2026 –
 * Jan 1, 2027", since the end's year alone would misdate the start.
 */
export function rangeText(range) {
	const start = range.start ?? range.from;
	if (start === range.to) return formatDayLong(range.to);
	const startYear = start.slice(0, 4);
	const toYear = range.to.slice(0, 4);
	const startText = startYear === toYear ? formatDay(start) : `${formatDay(start)}, ${startYear}`;
	return `${startText} – ${formatDay(range.to)}, ${toYear}`;
}

/** "Data starts Oct 9, 2026" when the preset was clamped to the panel's `dataFrom`; null otherwise. */
export function dataStartsText(range) {
	if (!range?.clamped || !range.dataFrom) return null;
	const d = parseDay(range.dataFrom);
	const text = new Intl.DateTimeFormat("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
		timeZone: "UTC",
	}).format(d);
	return `Data starts ${text}`;
}

/** The region to request: the query's when the panel declares it, else the panel's first. */
export function regionFor(panel, requested) {
	const regions = Array.isArray(panel?.regions) ? panel.regions : [];
	if (requested && regions.includes(requested)) return requested;
	return regions[0] ?? null;
}
